import type { JSX } from 'react';
import { useEngine } from '../../state/engineStore';

/** Minimal inline formatting: **bold** and blank-line paragraphs. Nothing else. */
function Prose({ text }: { text: string }): JSX.Element {
  return (
    <>
      {text.split('\n\n').map((para, i) => (
        <p key={i} style={{ margin: '0 0 10px', lineHeight: 1.55, fontSize: 12.5 }}>
          {para.split(/(\*\*[^*]+\*\*)/g).map((chunk, j) =>
            chunk.startsWith('**') && chunk.endsWith('**') ? (
              <strong key={j} style={{ color: 'var(--text)' }}>
                {chunk.slice(2, -2)}
              </strong>
            ) : (
              <span key={j}>{chunk}</span>
            ),
          )}
        </p>
      ))}
    </>
  );
}

export function LessonPanel({ onClose }: { onClose(): void }): JSX.Element | null {
  const lesson = useEngine((s) => s.snap?.lesson);
  const send = useEngine((s) => s.send);
  const openWindow = useEngine((s) => s.openWindow);
  if (!lesson) return null;

  const canAdvance = lesson.checkMet !== false;
  const last = lesson.stepIndex === lesson.totalSteps - 1;

  return (
    <aside
      className="scroll hmi-lesson"
      style={{
        width: 380,
        flexShrink: 0,
        borderLeft: '1px solid var(--line-dim)',
        background: 'var(--panel)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--line-dim)', display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <strong style={{ fontSize: 12 }}>
          Lesson {lesson.number} · {lesson.title}
        </strong>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={{ padding: '1px 7px', background: 'transparent', border: 'none' }}>
          ✕
        </button>
      </div>

      <div style={{ padding: '6px 12px 0' }}>
        <div style={{ display: 'flex', gap: 3 }}>
          {Array.from({ length: lesson.totalSteps }, (_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: i <= lesson.stepIndex ? 'var(--accent)' : 'var(--bg-deep)',
              }}
            />
          ))}
        </div>
        <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>
          Step {lesson.stepIndex + 1} of {lesson.totalSteps}
        </div>
      </div>

      <div className="scroll" style={{ flex: 1, padding: '10px 12px' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>{lesson.stepTitle}</h3>
        <Prose text={lesson.body} />
        {lesson.focusLoop && (
          <button style={{ marginTop: 4 }} onClick={() => openWindow('faceplate', lesson.focusLoop as string)}>
            Open {lesson.focusLoop}
          </button>
        )}
      </div>

      <div style={{ padding: '9px 12px', borderTop: '1px solid var(--line-dim)', display: 'flex', gap: 8, alignItems: 'center' }}>
        <button disabled={lesson.stepIndex === 0} onClick={() => send({ t: 'lessonStep', delta: -1 })}>
          Back
        </button>
        {lesson.checkMet === false && (
          <span className="muted" style={{ fontSize: 11, flex: 1 }}>
            Do the step above to continue.
          </span>
        )}
        {lesson.checkMet === true && (
          <span style={{ fontSize: 11, flex: 1, color: 'var(--ok)' }}>✓ Done</span>
        )}
        {lesson.checkMet === null && <span style={{ flex: 1 }} />}
        <button
          className={canAdvance ? 'on' : ''}
          disabled={!canAdvance}
          onClick={() => (last ? onClose() : send({ t: 'lessonStep', delta: 1 }))}
        >
          {last ? 'Finish' : 'Next'}
        </button>
      </div>
    </aside>
  );
}
